import { useState, useEffect, useCallback } from "react";
import useSupabase from "./useSupabase";
import { toast } from "sonner";
import { LogMessageUser } from "../components/LogsModal/LogsModal";

type LogEntry = {
  content: string;
  id?: string;
};

type ChatMessage = {
  id: string; // uuid
  conversation_id: string; // uuid
  role: LogMessageUser; // user | ai | loading (temporarily, will be removed for db)
  content: string;
  created_at: Date; // timestamptz
  log_id: string; // text
  project_id: string; // text
  isTemporaryLoading?: boolean; // Added for frontend loading state
};

interface UseChatMessagesProps {
  selectedLog: LogEntry | null;
  projectId: string | number | undefined;
  userId: string | undefined;
}

const useChatMessages = ({
  selectedLog,
  projectId,
  userId,
}: UseChatMessagesProps) => {
  const supabase = useSupabase();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Effect to handle conversation creation/fetching and initial messages
  useEffect(() => {
    if (!supabase || !selectedLog?.id || !projectId || !userId) {
      setMessages([]); // Clear messages if dependencies are not met
      setConversationId(null);
      return;
    }

    const fetchOrCreateConversationAndMessages = async () => {
      setIsLoading(true);
      try {
        // Try to find an existing conversation
        const { data: existingConversations, error: convError } = await supabase
          .from("conversations")
          .select("id")
          .eq("project_id", projectId)
          .eq("user_id", userId)
          .single();

        let currentConversationId = existingConversations?.id;

        if (!currentConversationId) {
          // Create a new conversation if none exists
          const { data: newConversation, error: newConvError } = await supabase
            .from("conversations")
            .insert({
              user_id: userId,
              title: `Conversation for log: ${selectedLog.id}`,
              log_id: selectedLog.id,
              project_id: projectId,
            })
            .select("id")
            .single();

          if (newConvError) throw newConvError;
          currentConversationId = newConversation?.id;
        }

        if (currentConversationId) {
          setConversationId(currentConversationId);

          // Fetch initial messages for the conversation
          const { data: initialMessages, error: messagesError } = await supabase
            .from("messages")
            .select("*")
            .eq("conversation_id", currentConversationId)
            .order("created_at", { ascending: true });

          if (messagesError) throw messagesError;
          const problematicMessageContent =
            "ERROR: failed to solve: Canceled: context canceled";
          const filteredMessages = (initialMessages || []).filter(
            (msg: ChatMessage) =>
              msg.content.trim() !== problematicMessageContent.trim(),
          );
          setMessages(filteredMessages || []);
        }
      } catch (error) {
        console.error(
          "Error fetching/creating conversation or messages:",
          error,
        );
        toast.error("Failed to load chat history.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrCreateConversationAndMessages();
  }, [supabase, selectedLog, projectId, userId]);

  // Effect for realtime message subscription
  useEffect(() => {
    if (!supabase || !conversationId) return;

    const messagesChannel = supabase
      .channel(`conversation_${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "*", // Listen to INSERT, UPDATE, DELETE
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload: any) => {
          // Handle new messages or updates
          if (payload.eventType === "INSERT") {
            setMessages((prev) => [...prev, payload.new]);
          } else if (payload.eventType === "UPDATE") {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === payload.old.id ? payload.new : msg,
              ),
            );
          } else if (payload.eventType === "DELETE") {
            setMessages((prev) =>
              prev.filter((msg) => msg.id !== payload.old.id),
            );
          }
        },
      )
      .subscribe();

    return () => {
      messagesChannel.unsubscribe();
    };
  }, [supabase, conversationId]);

  return { messages, conversationId, isLoading, setMessages };
};

export default useChatMessages;
